import type { PayerWizardProgress, RuleReviewSummary, PlanLaneConfig } from './types';
import { validateLaneConfig } from './laneTypes';
import type { ChecklistItem } from '@/components/payer-wizard/SetupCompletionChecklist';

export interface ChecklistStatus {
  items: ChecklistItem[];
  canActivate: boolean;
  blockerMessages: string[];
}

/**
 * Generate the completion checklist based on current wizard progress.
 * Used in both the header and review step.
 */
export function generateChecklist(
  progress: PayerWizardProgress | null,
  ruleReview: Record<string, RuleReviewSummary>
): ChecklistStatus {
  if (!progress) {
    return {
      items: [],
      canActivate: false,
      blockerMessages: ['Wizard not initialized'],
    };
  }
  
  const items: ChecklistItem[] = [];
  const blockerMessages: string[] = [];
  
  // 1. At least 1 lane enabled (required)
  const laneValidation = checkLaneEnabled(progress);
  items.push({
    id: 'lane_enabled',
    label: 'At least 1 submission lane enabled',
    status: laneValidation.valid ? 'complete' : 'incomplete',
    required: true,
    message: laneValidation.valid ? undefined : laneValidation.error,
  });
  if (!laneValidation.valid) {
    blockerMessages.push('Enable at least one submission method (Portal, Fax/Phone, or PAS) to activate this plan.');
  }
  
  // 2. Active ruleset exists (required) - checked via ruleset_source
  const hasRuleset = progress.plans.length > 0 && progress.plans.every(p => p.ruleset_source);
  items.push({
    id: 'ruleset_exists',
    label: 'Active ruleset configured',
    status: hasRuleset ? 'complete' : 'incomplete',
    required: true,
    message: hasRuleset ? undefined : 'Clone a template',
  });
  if (!hasRuleset) {
    blockerMessages.push('An active ruleset is required. Clone a template ruleset to continue.');
  }
  
  // 3. Auth default set (required)
  const authDefaultSet = checkAuthDefault(progress, ruleReview);
  items.push({
    id: 'auth_default',
    label: 'Authorization default set (CONDITIONAL)',
    status: authDefaultSet ? 'complete' : 'incomplete',
    required: true,
  });
  if (!authDefaultSet) {
    blockerMessages.push('Authorization default must be configured. Templates include this by default.');
  }
  
  // 4. Required docs checklist exists (required)
  const docsExist = checkDocsExist(progress, ruleReview);
  items.push({
    id: 'docs_checklist',
    label: 'Required documents checklist exists',
    status: docsExist ? 'complete' : 'incomplete',
    required: true,
  });
  if (!docsExist) {
    blockerMessages.push('Required documents checklist must be defined.');
  }
  
  // 5. SLA + follow-up cadence present (required)
  const slaCadenceSet = checkSlaCadence(progress, ruleReview);
  items.push({
    id: 'sla_cadence',
    label: 'SLA + follow-up cadence configured',
    status: slaCadenceSet ? 'complete' : 'incomplete',
    required: true,
  });
  if (!slaCadenceSet) {
    blockerMessages.push('SLA and follow-up cadence must be configured.');
  }
  
  // 6. PAS config status (optional)
  const pasStatus = checkPasStatus(progress);
  items.push({
    id: 'pas_config',
    label: `PAS configuration: ${pasStatus.label}`,
    status: 'optional',
    required: false,
    message: pasStatus.enabled ? (pasStatus.connected ? 'Connected' : 'Not connected yet') : 'Disabled',
  });
  
  return {
    items,
    canActivate: blockerMessages.length === 0,
    blockerMessages,
  };
}

function checkLaneEnabled(progress: PayerWizardProgress): { valid: boolean; error?: string } {
  if (progress.plans.length === 0) {
    return { valid: false, error: 'No plans configured' };
  }
  
  for (let i = 0; i < progress.plans.length; i++) {
    const laneConfig = progress.plan_lane_configs[i];
    if (!laneConfig) {
      return { valid: false, error: `Plan "${progress.plans[i].plan_name}" has no lane config` };
    }
    
    const validation = validateLaneConfig(laneConfig);
    if (!validation.valid) {
      return { valid: false, error: `${progress.plans[i].plan_name}: ${validation.error}` };
    }
  }
  
  return { valid: true };
}

function checkAuthDefault(
  progress: PayerWizardProgress, 
  ruleReview: Record<string, RuleReviewSummary>
): boolean {
  // Templates always include auth defaults
  for (const plan of progress.plans) {
    if (plan.ruleset_source === 'EXISTING_PLAN') continue;
    const summary = ruleReview[plan.ruleset_source];
    if (!summary || !summary.auth_requirement_default) {
      return false;
    }
  }
  return progress.plans.length > 0;
}

function checkDocsExist(
  progress: PayerWizardProgress,
  ruleReview: Record<string, RuleReviewSummary>
): boolean {
  for (const plan of progress.plans) {
    if (plan.ruleset_source === 'EXISTING_PLAN') continue;
    const summary = ruleReview[plan.ruleset_source];
    if (!summary || !summary.doc_requirements || summary.doc_requirements.length === 0) {
      return false;
    }
  }
  return progress.plans.length > 0;
}

function checkSlaCadence(
  progress: PayerWizardProgress,
  ruleReview: Record<string, RuleReviewSummary>
): boolean {
  for (const plan of progress.plans) {
    if (plan.ruleset_source === 'EXISTING_PLAN') continue;
    const summary = ruleReview[plan.ruleset_source];
    if (!summary || !summary.sla_hours || !summary.follow_up_cadence_hours) {
      return false;
    }
  }
  return progress.plans.length > 0;
}

function checkPasStatus(progress: PayerWizardProgress): { 
  enabled: boolean; 
  connected: boolean; 
  label: string;
} {
  const hasPasEnabled = progress.plan_lane_configs.some(c => c?.lanes_enabled?.FHIR_PAS);
  
  if (!hasPasEnabled) {
    return { enabled: false, connected: false, label: 'PAS Disabled' };
  }
  
  // For now, PAS is always "Not connected yet" in wizard context
  return { enabled: true, connected: false, label: 'PAS Enabled' };
}
